/**
 * The Control Panel below 900px (phones and tablets): a calm list that
 * answers "is anything wrong on my pours?" at a glance. Desktop keeps
 * pages/Pours.tsx (DesktopPours) untouched.
 *
 * - The AppBar (through useMobileHeader) holds the title and Search; search
 *   opens a field under the bar and looks across every status (a debounced
 *   server search).
 * - Segmented tabs: In progress (with its count) | Pending | Completed.
 *   Tapping the selected tab again refreshes; so does pulling the list
 *   down from the top (usePullToRefresh).
 * - In progress lists every pour at once, as one list in the server's
 *   (desktop) order, scheduled pours included, then one folded line
 *   "Awaiting review" (probably finished; a person opens one and stops it
 *   by hand). Pending and Completed page by 25 (Load more).
 * - One soft card per pour on a grey page (MobilePourRow): the Pour
 *   Description and Pour ID, then "Project, start date" with the problem
 *   only when there is one. The readings, Stop Pour and the report live on
 *   the pour's dashboard.
 * - The list refreshes quietly every 5 minutes while visible and when the
 *   page comes back; a line appears only when the data is old or a refresh
 *   failed.
 * - The project map, only when opened from the bottom bar; its data loads
 *   only then (ControlPanelMap).
 * - A row tap opens the pour's dashboard (Overview) in one tap, carrying the
 *   list row in router state; nothing else is fetched from the list.
 * - Coming back (Back / POP) restores the tab, search, loaded pages, the
 *   list and the scroll position from the in-memory snapshot
 *   (controlPanelSession), refreshing quietly when it is stale or older
 *   than 60 s.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { poursAPI } from '../../services/pours';
import type { Pour } from '../../services/pours';
import { useAuth } from '../../context/AuthContext';
import { useMobileHeader } from '../../context/MobileHeaderContext';
import { useNow } from '../../utils/useNow';
import { formatAgo, formatLocalClock } from '../../utils/relativeTime';
import type { TranslateFn } from '../../utils/relativeTime';
import { apiErrorText } from '../../utils/apiError';
import MobileToast from '../mobile/MobileToast';
import { controlPanelUserKey, loadSnapshot, readSavedFilters, saveSnapshot } from './controlPanelSession';
import type { ControlPanelSnapshot } from './controlPanelSession';
import ControlPanelMap from './ControlPanelMap';
import MobilePourRow, { SOFT_CARD_SHADOW } from './MobilePourRow';
import { buildPourRow, groupPourRows } from './pourCardModel';
import type { PourRowModel } from './pourCardModel';
import StatusFilterBar, { coerceStatusFilter } from './StatusFilterBar';
import { PULL_THRESHOLD, usePullToRefresh } from './usePullToRefresh';
import type { MobileStatusFilter } from './StatusFilterBar';

/** Rows per server page (Load more) on Pending, Completed and search. */
const PAGE_SIZE = 25;
/** In progress loads every pour at once so the attention grouping is whole
 *  (a company has tens of live pours, not thousands). */
const IN_PROGRESS_PAGE_SIZE = 200;
/** The server's largest page: a refresh reloads every loaded page at once. */
const MAX_PAGE_SIZE = 1000;
/** The list refreshes quietly this often while the page is visible. */
const POLL_MS = 5 * 60 * 1000;
/** Data older than this says so (two polls missed). */
const STALE_MS = 10 * 60 * 1000;
/** How long the search box waits after the last key before asking the
 *  server. */
const SEARCH_DEBOUNCE_MS = 300;
/** A restored list older than this is refreshed quietly on return. */
const SNAPSHOT_FRESH_MS = 60 * 1000;
/** How many placeholder rows a first load shows. */
const SKELETON_ROWS = 6;

/** 'replace' a new tab or search (page 0), 'more' the next page, and
 *  'refresh' every loaded page again, quietly. */
type LoadKind = 'replace' | 'more' | 'refresh';

/** The list shown and the query it answers. */
interface ListState {
  pours: Pour[];
  total: number;
  pagesLoaded: number;
  /** Epoch ms of the fetch; 0 = nothing loaded yet. */
  fetchedAt: number;
  statusFilter: MobileStatusFilter;
  search: string;
}

const emptyList = (statusFilter: MobileStatusFilter, search: string): ListState => ({
  pours: [],
  total: 0,
  pagesLoaded: 0,
  fetchedAt: 0,
  statusFilter,
  search,
});

const queryKey = (statusFilter: string, search: string) => `${statusFilter}\n${search}`;

/** The server query for a tab and search: a search looks across every
 *  status; In progress loads in one page. */
const serverQuery = (statusFilter: MobileStatusFilter, search: string) => ({
  status: search ? 'All' : statusFilter,
  pageSize: !search && statusFilter === 'In progress' ? IN_PROGRESS_PAGE_SIZE : PAGE_SIZE,
});

const emptyTitle = (list: ListState, t: TranslateFn): string => {
  if (list.search) {
    return t('mobile.controlPanel.emptySearch', { defaultValue: 'No pours match "{{search}}"', search: list.search });
  }
  switch (list.statusFilter) {
    case 'Pending':
      return t('mobile.controlPanel.emptyPending', { defaultValue: 'No pending pours' });
    case 'Complete':
      return t('mobile.controlPanel.emptyComplete', { defaultValue: 'No completed pours' });
    default:
      return t('mobile.controlPanel.emptyInProgress', { defaultValue: 'No pours in progress' });
  }
};

/** A placeholder card while the first page loads, shaped like a pour card:
 *  the name and ID tag, then the project line. */
const SkeletonRow: React.FC<{ index: number }> = ({ index }) => (
  <Box sx={{ bgcolor: 'background.paper', borderRadius: '18px', boxShadow: SOFT_CARD_SHADOW, px: '18px', py: '17px', mb: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Skeleton variant="rounded" width={`${[58, 70, 46, 62, 54, 66][index % 6]}%`} height={16} />
      <Box sx={{ flex: 1 }} />
      <Skeleton variant="rounded" width={68} height={20} />
    </Box>
    <Skeleton variant="rounded" width={`${[44, 38, 52, 40, 47, 35][index % 6]}%`} height={13} sx={{ mt: '9px' }} />
  </Box>
);

/** A group of pour cards. */
const RowSection: React.FC<{
  testId: string;
  rows: Array<{ pour: Pour; model: PourRowModel }>;
  now: number;
  freshnessNow: number;
  onOpen: (pour: Pour) => void;
}> = ({ testId, rows, now, freshnessNow, onOpen }) => {
  if (rows.length === 0) return null;
  return (
    <Box component="section" data-testid={testId} sx={{ mt: 1.5 }}>
      {rows.map(({ pour }) => (
        <MobilePourRow key={pour.PourID} pour={pour} now={now} freshnessNow={freshnessNow} onOpen={onOpen} />
      ))}
    </Box>
  );
};

/** The probably finished pours, folded into one line at the bottom: past
 *  the planned end, no data for over a day, or logging ended. Only a person
 *  stops a pour. */
const ReviewSection: React.FC<{
  rows: Array<{ pour: Pour; model: PourRowModel }>;
  open: boolean;
  onToggle: () => void;
  now: number;
  freshnessNow: number;
  onOpen: (pour: Pour) => void;
}> = ({ rows, open, onToggle, now, freshnessNow, onOpen }) => {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  return (
    <Box component="section" data-testid="section-review" sx={{ mt: 0.5 }}>
      <ButtonBase
        onClick={onToggle}
        aria-expanded={open}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.75,
          py: 1.5,
          minHeight: 56,
          textAlign: 'left',
          bgcolor: 'background.paper',
          borderRadius: '18px',
          boxShadow: SOFT_CARD_SHADOW,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 650 }}>
            {t('mobile.controlPanel.section.review', {
              defaultValue: 'Awaiting review · {{n}}',
              n: rows.length,
            })}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {t('mobile.controlPanel.section.reviewHint', {
              defaultValue: 'Past the planned end, no data for over 24 h, or logging ended. Open one to review and stop it.',
            })}
          </Typography>
        </Box>
        <ExpandMoreIcon
          aria-hidden
          sx={{ color: 'text.secondary', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
        />
      </ButtonBase>
      {open && (
        <Box sx={{ mt: 1.25 }}>
          {rows.map(({ pour }) => (
            <MobilePourRow key={pour.PourID} pour={pour} now={now} freshnessNow={freshnessNow} onOpen={onOpen} muted />
          ))}
        </Box>
      )}
    </Box>
  );
};

const MobileControlPanel: React.FC = () => {
  const { t } = useTranslation();
  const tRef = useRef<TranslateFn>(t);
  tRef.current = t;
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { user } = useAuth();
  const userKey = controlPanelUserKey(user);
  const userKeyRef = useRef(userKey);
  userKeyRef.current = userKey;
  const now = useNow(60000);

  // Where the page starts: the snapshot (Back from a dashboard, or the tab
  // still open), else the tab and search saved in this browser tab, else
  // the defaults. Read once, at mount.
  const [seed] = useState(() => {
    const snapshot = loadSnapshot(userKey);
    const saved = snapshot ? null : readSavedFilters(userKey);
    const statusFilter = coerceStatusFilter(snapshot?.statusFilter ?? saved?.statusFilter);
    const search = (snapshot?.search ?? saved?.search ?? '').trim();
    // A list saved under another filter (All, Archived) is not shown.
    const withData: ControlPanelSnapshot | null =
      snapshot && snapshot.fetchedAt > 0 && snapshot.statusFilter === statusFilter ? snapshot : null;
    return { statusFilter, search, snapshot: withData };
  });

  const [statusFilter, setStatusFilter] = useState<MobileStatusFilter>(seed.statusFilter);
  // What is typed, and the search the server was asked for (debounced).
  const [searchText, setSearchText] = useState(seed.search);
  const [search, setSearch] = useState(seed.search);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const [list, setList] = useState<ListState>(() =>
    seed.snapshot
      ? {
          pours: seed.snapshot.pours,
          total: seed.snapshot.total,
          pagesLoaded: seed.snapshot.pagesLoaded,
          fetchedAt: seed.snapshot.fetchedAt,
          statusFilter: seed.statusFilter,
          search: seed.search,
        }
      : emptyList(seed.statusFilter, seed.search),
  );
  const listRef = useRef(list);
  listRef.current = list;
  const queryRef = useRef({ statusFilter, search });
  queryRef.current = { statusFilter, search };

  // The request running, if any; only the newest request may change the list.
  const [busy, setBusy] = useState<LoadKind | null>(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  // Nothing to show: the first load (or a tab change) failed.
  const [loadError, setLoadError] = useState('');
  // A quiet refresh failed: the list shown is from this fetch.
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; text: string; severity: 'success' | 'error' }>({
    open: false,
    text: '',
    severity: 'success',
  });
  // Bumped by the poll and refreshes: the open map reloads its pins.
  const [mapRefresh, setMapRefresh] = useState(0);
  // The pours awaiting review start folded away.
  const [reviewOpen, setReviewOpen] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const showMessage = useCallback((text: string, severity: 'success' | 'error') => {
    setToast({ open: true, text, severity });
  }, []);

  const load = useCallback(async (kind: LoadKind) => {
    const query = queryRef.current;
    const current = listRef.current;
    const request = ++requestRef.current;
    setBusy(kind);
    if (kind === 'replace') setLoadError('');
    const { status, pageSize: basePageSize } = serverQuery(query.statusFilter, query.search);
    const page = kind === 'more' ? current.pagesLoaded : 0;
    const pageSize =
      kind === 'refresh'
        ? Math.min(basePageSize * Math.max(1, current.pagesLoaded), MAX_PAGE_SIZE)
        : basePageSize;
    try {
      const response = await poursAPI.getPourPage({
        page,
        pageSize,
        status,
        search: query.search,
        healthDetail: true,
      });
      if (request !== requestRef.current || !mountedRef.current) return;
      const rows = response.pours || [];
      let pours: Pour[];
      let pagesLoaded: number;
      if (kind === 'more') {
        // A pour created meanwhile shifts the pages: never list one twice.
        const seen = new Set(current.pours.map((p) => p.PourID));
        pours = [...current.pours, ...rows.filter((p) => !seen.has(p.PourID))];
        pagesLoaded = current.pagesLoaded + 1;
      } else {
        pours = rows;
        pagesLoaded = kind === 'refresh' ? Math.max(1, current.pagesLoaded) : 1;
      }
      const next: ListState = {
        pours,
        total: response.total ?? pours.length,
        pagesLoaded,
        fetchedAt: Date.now(),
        statusFilter: query.statusFilter,
        search: query.search,
      };
      listRef.current = next;
      setList(next);
      setLoadError('');
      setRefreshFailed(false);
      saveSnapshot({ userKey: userKeyRef.current, ...next, stale: false });
    } catch (err: any) {
      if (request !== requestRef.current || !mountedRef.current) return;
      const tr = tRef.current;
      if (kind === 'more') {
        showMessage(
          apiErrorText(err, tr('mobile.controlPanel.loadMoreFailed', { defaultValue: 'Could not load more pours' })),
          'error',
        );
      } else if (kind === 'refresh' && current.fetchedAt > 0) {
        // Keep what is shown, labelled with its age.
        setRefreshFailed(true);
      } else {
        // The rows shown belong to another tab: drop them.
        const empty = emptyList(query.statusFilter, query.search);
        listRef.current = empty;
        setList(empty);
        setLoadError(apiErrorText(err, tr('mobile.controlPanel.loadFailed', { defaultValue: 'Could not load pours' })));
      }
    } finally {
      if (request === requestRef.current && mountedRef.current) setBusy(null);
    }
  }, [showMessage]);

  // A new tab or search loads its first page. The restored snapshot's own
  // query is already loaded.
  const loadedKeyRef = useRef<string | null>(seed.snapshot ? queryKey(seed.statusFilter, seed.search) : null);
  useEffect(() => {
    const key = queryKey(statusFilter, search);
    if (loadedKeyRef.current === key) return;
    loadedKeyRef.current = key;
    setRefreshFailed(false);
    // The saved list belongs to the old query: forget it until the new
    // one loads (the tab and search are kept for a reloaded browser tab).
    saveSnapshot({
      userKey: userKeyRef.current,
      statusFilter,
      search,
      pours: [],
      total: 0,
      pagesLoaded: 0,
      fetchedAt: 0,
      stale: false,
    });
    load('replace');
  }, [statusFilter, search, load]);

  // A restored list that is stale (a stop elsewhere) or older than a
  // minute refreshes quietly, without a spinner over it.
  const seedRefreshRef = useRef(false);
  useEffect(() => {
    if (seedRefreshRef.current || !seed.snapshot) return;
    seedRefreshRef.current = true;
    if (seed.snapshot.stale || Date.now() - seed.snapshot.fetchedAt > SNAPSHOT_FRESH_MS) load('refresh');
  }, [seed, load]);

  // The search box asks the server once typing pauses.
  useEffect(() => {
    const term = searchText.trim();
    if (term === search) return undefined;
    const timer = window.setTimeout(() => setSearch(term), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchText, search]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const refresh = useCallback(() => {
    if (busyRef.current) return;
    setMapRefresh((n) => n + 1);
    load(listRef.current.fetchedAt > 0 ? 'refresh' : 'replace');
  }, [load]);

  // Poll every 5 minutes while the page is visible, quietly. A browser tab
  // that comes back after a longer absence refreshes straight away. A
  // request already running is not doubled.
  const pollRef = useRef<() => void>(() => {});
  pollRef.current = () => {
    if (busyRef.current) return;
    setMapRefresh((n) => n + 1);
    load('refresh');
  };
  useEffect(() => {
    const visible = () => document.visibilityState !== 'hidden';
    const timer = window.setInterval(() => {
      if (visible()) pollRef.current();
    }, POLL_MS);
    const onVisibilityChange = () => {
      if (visible() && Date.now() - listRef.current.fetchedAt >= POLL_MS) pollRef.current();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // Back from a dashboard (POP): return to where the user was. The shell
  // scrolls to the top only on PUSH / REPLACE.
  useLayoutEffect(() => {
    const y = seed.snapshot?.scrollY ?? 0;
    if (navigationType !== 'POP' || y <= 0) return undefined;
    window.scrollTo(0, y);
    // The page may still be settling: try once more after it paints.
    const frame = window.requestAnimationFrame(() => {
      if (Math.abs(window.scrollY - y) > 1) window.scrollTo(0, y);
    });
    return () => window.cancelAnimationFrame(frame);
    // Once, at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leaving the page keeps the scroll position for a later Back. A layout
  // cleanup, so it reads the position before the next page resets it; only
  // into a snapshot that still exists (never after a logout cleared it).
  useLayoutEffect(
    () => () => {
      const key = userKeyRef.current;
      if (loadSnapshot(key)) saveSnapshot({ userKey: key, scrollY: window.scrollY });
    },
    [],
  );

  const openPour = useCallback(
    (pour: Pour) => {
      saveSnapshot({ userKey: userKeyRef.current, scrollY: window.scrollY });
      navigate(`/pours/${pour.PourID}/dashboard`, { state: { from: 'control-panel', listPour: pour } });
    },
    [navigate],
  );

  const openSearch = useCallback(() => {
    if (searchOpen) searchInputRef.current?.focus();
    else setSearchOpen(true);
  }, [searchOpen]);

  const clearSearch = () => {
    setSearchText('');
    setSearch('');
    setSearchOpen(false);
  };

  // "View pours" on a map pin: search the list for that project at once.
  const viewProjectPours = useCallback((projectName: string) => {
    setSearchText(projectName);
    setSearch(projectName.trim());
  }, []);

  // Changing tab (or tapping the selected one) starts at the top.
  const changeTab = useCallback((next: MobileStatusFilter) => {
    setStatusFilter(next);
    window.scrollTo(0, 0);
  }, []);
  const reselectTab = useCallback(() => {
    window.scrollTo(0, 0);
    refresh();
  }, [refresh]);

  // Pull down from the top to refresh; the line under the tabs spins until
  // that refresh is done.
  const [pullRefreshing, setPullRefreshing] = useState(false);
  const pullRefresh = useCallback(() => {
    if (busyRef.current) return;
    setPullRefreshing(true);
    refresh();
  }, [refresh]);
  useEffect(() => {
    if (busy === null) setPullRefreshing(false);
  }, [busy]);
  const pull = usePullToRefresh(pullRefresh);

  const searchActive = searchText.trim() !== '' || search !== '';
  const isBusy = busy !== null;
  const title = t('nav.controlPanel', 'Control Panel');
  const searchLabel = t('common.search', 'Search');
  useMobileHeader(
    {
      title,
      actions: [
        { key: 'search', label: searchLabel, icon: <SearchIcon />, onClick: openSearch, active: searchActive },
      ],
    },
    [title, searchLabel, searchActive],
  );

  const loaded = list.fetchedAt > 0;
  const replacing = busy === 'replace';
  const showSkeleton = !loadError && (!loaded || (replacing && list.pours.length === 0));
  const searchRowVisible = searchOpen || searchText !== '';
  // A search shows its results flat, across every status.
  const searching = list.search !== '';
  const freshnessNow = list.fetchedAt || now;

  const rows = useMemo(
    () => list.pours.map((pour) => ({ pour, model: buildPourRow(pour, now, t, { freshnessNow }) })),
    [list.pours, now, t, freshnessNow],
  );
  const groups = useMemo(() => groupPourRows(rows), [rows]);
  const grouped = !searching && list.statusFilter === 'In progress';

  const stale = loaded && !refreshFailed && now - list.fetchedAt > STALE_MS;

  return (
    <Box sx={{ mx: { xs: -1, sm: 0 } }}>
      {searchRowVisible && (
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          value={searchText}
          inputRef={searchInputRef}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => {
            // Enter searches at once, without the debounce.
            if (e.key === 'Enter') {
              setSearch(searchText.trim());
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder={t('mobile.controlPanel.searchPlaceholder', { defaultValue: 'Search pours' })}
          inputProps={{
            'aria-label': t('mobile.controlPanel.searchPlaceholder', { defaultValue: 'Search pours' }),
            enterKeyHint: 'search',
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  onClick={clearSearch}
                  aria-label={t('mobile.controlPanel.clearSearch', { defaultValue: 'Clear search' })}
                  sx={{ width: 44, height: 44 }}
                >
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1, '& .MuiInputBase-root': { minHeight: 48, bgcolor: 'background.paper', borderRadius: '12px' } }}
        />
      )}

      {searching ? (
        <Typography sx={{ px: 0.5, py: 1.25, fontSize: '0.875rem', fontWeight: 600, color: 'text.secondary' }}>
          {t('mobile.controlPanel.countMatching', { defaultValue: '{{n}} matching', n: list.total })}
        </Typography>
      ) : (
        <StatusFilterBar
          value={statusFilter}
          onChange={changeTab}
          count={loaded && list.statusFilter === statusFilter ? list.total : null}
          onReselect={reselectTab}
        />
      )}

      {(pull > 0 || pullRefreshing) && (
        <Box
          role={pullRefreshing ? 'status' : undefined}
          data-testid="pull-refresh"
          sx={{
            height: pullRefreshing ? 36 : pull,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            fontSize: '0.75rem',
            color: 'text.secondary',
            transition: pull > 0 ? 'none' : 'height 0.2s',
          }}
        >
          <CircularProgress
            size={14}
            thickness={5}
            variant={pullRefreshing ? 'indeterminate' : 'determinate'}
            value={Math.min(100, (pull / PULL_THRESHOLD) * 100)}
          />
          {pullRefreshing
            ? t('mobile.controlPanel.refreshing', { defaultValue: 'Refreshing' })
            : pull >= PULL_THRESHOLD
            ? t('mobile.controlPanel.releaseToRefresh', { defaultValue: 'Release to refresh' })
            : t('mobile.controlPanel.pullToRefresh', { defaultValue: 'Pull to refresh' })}
        </Box>
      )}

      {/* A 2px slot, so the page does not jump when a request starts. */}
      <Box sx={{ height: 2 }}>
        {isBusy && (
          <LinearProgress
            sx={{ height: 2 }}
            aria-label={t('mobile.controlPanel.loading', { defaultValue: 'Loading pours' })}
          />
        )}
      </Box>

      {loaded && (refreshFailed || stale) && (
        <Box
          role="status"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, px: 0.5, color: '#8a5300' }}
        >
          <WarningAmberIcon sx={{ fontSize: 20, flexShrink: 0 }} />
          <Typography sx={{ flex: 1, fontSize: '0.875rem', fontWeight: 600 }}>
            {refreshFailed
              ? t('mobile.controlPanel.updateFailed', {
                  defaultValue: 'Could not update. Showing data from {{time}}',
                  time: formatLocalClock(list.fetchedAt, now),
                })
              : t('mobile.controlPanel.updated', {
                  defaultValue: 'Updated {{ago}}',
                  ago: formatAgo(list.fetchedAt, now, t),
                })}
          </Typography>
          <Button
            onClick={refresh}
            disabled={isBusy}
            sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600 }}
          >
            {refreshFailed
              ? t('mobile.controlPanel.retry', { defaultValue: 'Retry' })
              : t('common.refresh', 'Refresh')}
          </Button>
        </Box>
      )}

      <ControlPanelMap
        status={search ? 'All' : statusFilter}
        search={search}
        refreshSignal={mapRefresh}
        onViewPours={viewProjectPours}
      />

      {loadError ? (
        <Alert
          severity="error"
          sx={{ mt: 2, alignItems: 'center' }}
          action={
            <Button color="inherit" onClick={() => load('replace')} sx={{ minHeight: 44, textTransform: 'none' }}>
              {t('mobile.controlPanel.retry', { defaultValue: 'Retry' })}
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : showSkeleton ? (
        <Box
          role="status"
          aria-busy="true"
          aria-label={t('mobile.controlPanel.loading', { defaultValue: 'Loading pours' })}
          sx={{ mt: 1.5 }}
        >
          {Array.from({ length: SKELETON_ROWS }, (_, i) => (
            <SkeletonRow key={i} index={i} />
          ))}
        </Box>
      ) : list.pours.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>{emptyTitle(list, t)}</Typography>
          {searching && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('mobile.controlPanel.emptySearchHint', { defaultValue: 'Try the project name or the pour code' })}
            </Typography>
          )}
        </Box>
      ) : (
        <Box aria-busy={replacing} sx={{ opacity: replacing ? 0.55 : 1, transition: 'opacity 0.2s' }}>
          {grouped ? (
            <>
              <RowSection testId="section-list" rows={groups.list} now={now} freshnessNow={freshnessNow} onOpen={openPour} />
              <RowSection testId="section-other" rows={groups.other} now={now} freshnessNow={freshnessNow} onOpen={openPour} />
              <ReviewSection
                rows={groups.review}
                open={reviewOpen}
                onToggle={() => setReviewOpen((v) => !v)}
                now={now}
                freshnessNow={freshnessNow}
                onOpen={openPour}
              />
            </>
          ) : (
            <RowSection testId="section-list" rows={rows} now={now} freshnessNow={freshnessNow} onOpen={openPour} />
          )}
        </Box>
      )}

      {loaded && !loadError && list.pours.length > 0 && list.pours.length < list.total && (
        <Button
          fullWidth
          variant="outlined"
          onClick={() => load('more')}
          disabled={isBusy}
          startIcon={busy === 'more' ? <CircularProgress size={18} color="inherit" /> : undefined}
          sx={{ mt: 2, minHeight: 48, textTransform: 'none', fontWeight: 600 }}
        >
          {t('mobile.controlPanel.loadMore', {
            defaultValue: 'Load more ({{shown}} of {{total}})',
            shown: list.pours.length,
            total: list.total,
          })}
        </Button>
      )}

      <MobileToast
        open={toast.open}
        message={toast.text}
        severity={toast.severity}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default MobileControlPanel;
